import React from 'react';
import { t } from '@citrite/translate';
import {
	CheckmarkInCircleSolidIcon,
	CircleIcon,
	ExclamationInDiamondSolidIcon,
	IconBadge,
	IInCircleSolidIcon,
	layout,
	LoadingInverted,
	NotificationBadgeHalfIcon,
	NotificationBadgeIcon,
	palette,
	SettingsWrenchSolidIcon,
	Tooltip,
	typography,
} from '@citrite/web-ui-component';
import { SkeletonLoaderShapes } from '@citrite/workspace-ui-platform-react';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import moment from 'moment';
import { PowerState } from 'App/Activity/Network/ActionsUtil';
import { Session, UNKNOWN_STATE } from 'App/Activity/ResourceManagerContext';
import { useDesktopDetails } from 'App/Activity/useDesktopDetails';
import { useResourceActions } from 'App/Activity/useResourceActions';
import { useTransitioningStatus } from 'App/Activity/useTransitioningStatus';
import { getTimestamp } from 'App/Activity/Utility';
import { TROUBLESHOOTING_STATUS } from 'App/ProgressAlert/utils';
import { getResourceSubText } from 'App/Screen/util';
import { isLaunchStatus, removeEllipsis } from 'App/Tile/utils';
import { detectLanguage } from 'javascript/sf/detectLanguage';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

const { LoadingBar } = SkeletonLoaderShapes;
const SHINE_START_POSITION = '-5%';
const SHINE_END_POSITION = '110%';
const POWER_STATE_BADGE_MAX_WIDTH = '128px';

interface Props {
	resource: Resource;
	powerState: PowerState;
	lastUpdatedTime: string;
	canShowTroubleshootStatus?: boolean;
}

const Wrapper = styled.div`
	display: block;
`;

const BadgeContainer: React.FC<React.ComponentProps<typeof Wrapper>> = props => (
	<Wrapper data-testid="detailed-desktop-tile-power-badge" {...props} />
);

const LoadingBarWrapper = styled.div`
	height: ${layout.space22};
	display: flex;
	align-items: center;
`;

const CustomIconBadge = styled(IconBadge)`
	height: ${layout.space22};
	font-size: ${typography.sizes.xsml2};
	line-height: normal;
	padding: ${layout.tinySpace} ${layout.space6};
	display: inline-flex;
	align-items: center;
	text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
	max-width: ${POWER_STATE_BADGE_MAX_WIDTH};
`;

const TransitionBadge = styled(CustomIconBadge)`
	gap: ${layout.space2};
`;

const BadgeText = styled.span`
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	height: ${layout.space12};
	.detailed-desktop-tile-container:hover & {
		text-decoration: underline dotted;
		text-underline-offset: 1px;
	}
`;

const shineMove = keyframes`
	0% {
		left: ${SHINE_START_POSITION};
	}
	15% {
		left: ${SHINE_END_POSITION};
	}
	100% {
		left: ${SHINE_END_POSITION};
	}
`;

const ShineWrapper = styled.div<{ shouldShowAnimation?: boolean }>`
	position: relative;

	${({ shouldShowAnimation }) =>
		shouldShowAnimation &&
		css`
			overflow: hidden;
			&::before {
				content: '';
				position: absolute;
				left: ${SHINE_START_POSITION};
				top: -2px;
				width: ${layout.tinySpace};
				height: ${layout.space32};
				background: rgba(255, 255, 255, 0.7);
				transform: rotate(28deg);
				filter: blur(4px);
				animation: ${shineMove} 3s ease-out infinite;
			}
		`}
`;

const iconSize = 10;
const textColor = palette.white;

interface BadgeWithTooltipProps {
	icon: React.ReactNode;
	backgroundColor: string;
	text: string;
	tooltipText: string;
	isDisabled?: boolean;
	onMouseEnter?: () => void;
}

const BadgeWithTooltip: React.FC<BadgeWithTooltipProps> = ({
	icon,
	backgroundColor,
	text,
	tooltipText,
	isDisabled = false,
	onMouseEnter,
}) => (
	<Tooltip
		hoverTrigger={
			<BadgeContainer onMouseEnter={onMouseEnter}>
				<CustomIconBadge
					icon={icon}
					backgroundColor={backgroundColor}
					textColor={textColor}
					children={<BadgeText>{text}</BadgeText>}
				/>
			</BadgeContainer>
		}
		isDisabled={isDisabled}
	>
		{tooltipText}
	</Tooltip>
);

interface TransitionStateBadgeProps {
	transitionText: string;
	tooltipText?: string;
}

const TransitionStateBadge: React.FC<TransitionStateBadgeProps> = ({
	transitionText,
}) => (
	<BadgeContainer>
		<TransitionBadge
			icon={<LoadingInverted size={iconSize} />}
			backgroundColor={palette.teal700}
			textColor={textColor}
		>
			<BadgeText>{transitionText}</BadgeText>
		</TransitionBadge>
	</BadgeContainer>
);

export const PowerStateBadge: React.FC<Props> = ({
	resource,
	powerState,
	lastUpdatedTime,
	canShowTroubleshootStatus = false,
}) => {
	const [powerStateTimeStamp, setpowerStateTimeStamp] = React.useState<string>('');
	const { maintenanceMode, summaryState } = useDesktopDetails(resource);
	const lastUpdatedTimestamp = getTimestamp(lastUpdatedTime);

	const resourceSession = useResourceActions(resource);
	const transitioningStatus = useTransitioningStatus(
		resourceSession.session as Session,
		resource
	);

	const getPowerStatusAge = () => {
		if (powerState === PowerState.UNMANAGED) {
			return t('Workspace:activity_manager.tooltip_state_unmanaged');
		}
		if (powerState === PowerState.UNKNOWN || powerState === PowerState.NOT_SUPPORTED) {
			return t('Workspace:activity_manager.tooltip_badge_pooled_desktop');
		}

		const time = moment(lastUpdatedTimestamp).locale(detectLanguage()).format('LT');
		return t('Workspace:activity_manager.tooltip_state_last_updated', { time });
	};

	const handlePowerStatusAge = () => {
		const machineStatusAge = getPowerStatusAge();
		setpowerStateTimeStamp(machineStatusAge);
	};

	// 1. Prioritize loading state
	if (powerState === PowerState.LOADING) {
		return (
			<BadgeContainer>
				<LoadingBarWrapper>
					<LoadingBar
						width="80px"
						topMargin={layout.tinySpace}
						bottomMargin={layout.tinySpace}
						leftMargin={layout.space2}
					/>
				</LoadingBarWrapper>
			</BadgeContainer>
		);
	}

	// 2. Prioritize troubleshooting transitioning state
	if (transitioningStatus === TROUBLESHOOTING_STATUS) {
		return (
			<TransitionStateBadge
				transitionText={t(`Workspace:launch_status.${transitioningStatus}`)}
			/>
		);
	}

	// 3. Prioritize troubleshoot status
	if (canShowTroubleshootStatus) {
		return (
			<BadgeWithTooltip
				icon={<ExclamationInDiamondSolidIcon size={iconSize} color={palette.white} />}
				backgroundColor={palette.orange800}
				text={t('Workspace:inspect_connection.troubleshoot')}
				tooltipText={t('Workspace:inspect_connection.troubleshoot_tooltip')}
			/>
		);
	}

	// 4. Prioritize transitioning status
	if (transitioningStatus && transitioningStatus !== UNKNOWN_STATE) {
		const transitionText = isLaunchStatus(transitioningStatus)
			? t(`Workspace:launch_status.${transitioningStatus}`)
			: removeEllipsis(transitioningStatus);

		return <TransitionStateBadge transitionText={transitionText} />;
	}

	// 5. Final state determination
	let stateKey: string;
	if (maintenanceMode) {
		// 5.a Prioritize maintenance mode
		stateKey = 'UnderMaintenance';
	} else if (summaryState === 'InUse') {
		// 5.b Next prioritize InUse state
		stateKey = 'Active';
	} else if (
		// 5.c Fallbacks for NOT_SUPPORTED, UNKNOWN, UNMANAGED
		powerState === PowerState.NOT_SUPPORTED ||
		powerState === PowerState.UNKNOWN ||
		powerState === PowerState.UNMANAGED
	) {
		stateKey = PowerState.UNKNOWN;
	} else {
		stateKey = powerState; // 5.d Otherwise use the actual power state
	}

	const powerStateConfig: Record<
		string,
		{ icon: React.ComponentType<any>; backgroundColor: string; text: string }
	> = {
		[PowerState.OFF]: {
			icon: CircleIcon,
			backgroundColor: palette.grey600,
			text: t('Workspace:activity_manager.powered_off'),
		},
		[PowerState.ON]: {
			icon: CircleIcon,
			backgroundColor: palette.green900,
			text: t('Workspace:activity_manager.powered_on'),
		},
		[PowerState.READY]: {
			icon: CheckmarkInCircleSolidIcon,
			backgroundColor: palette.green900,
			text: t('Workspace:activity_manager.ready'),
		},
		[PowerState.UNAVAILABLE]: {
			icon: ExclamationInDiamondSolidIcon,
			backgroundColor: palette.grey700,
			text: t('Workspace:activity_manager.unavailable'),
		},
		[PowerState.SUSPENDED]: {
			icon: NotificationBadgeHalfIcon,
			backgroundColor: palette.yellow950,
			text: t('Workspace:activity_manager.powerstate_in_Hibernation'),
		},
		[PowerState.SUSPENDING]: {
			icon: LoadingInverted,
			backgroundColor: palette.teal700,
			text: removeEllipsis(t('Workspace:activity_manager.hibernating')),
		},
		[PowerState.TURNING_OFF]: {
			icon: LoadingInverted,
			backgroundColor: palette.teal700,
			text: removeEllipsis(t('Workspace:activity_manager.shutting_down')),
		},
		[PowerState.TURNING_ON]: {
			icon: LoadingInverted,
			backgroundColor: palette.teal700,
			text: removeEllipsis(t('Workspace:launch_status.powering-on')),
		},
		[PowerState.STARTING]: {
			icon: LoadingInverted,
			backgroundColor: palette.teal700,
			text: removeEllipsis(t('Workspace:activity_manager.starting')),
		},
		[PowerState.RESUMING]: {
			icon: LoadingInverted,
			backgroundColor: palette.teal700,
			text: removeEllipsis(t('Workspace:activity_manager.resuming')),
		},
		UnderMaintenance: {
			icon: SettingsWrenchSolidIcon,
			backgroundColor: palette.grey700,
			text: t('Workspace:activity_manager.under_maintenance'),
		},
		Active: {
			icon: NotificationBadgeIcon,
			backgroundColor: palette.green900,
			text: t('Workspace:activity_manager.active'),
		},
		[PowerState.UNKNOWN]: {
			icon: IInCircleSolidIcon,
			backgroundColor: palette.blue900,
			text: getResourceSubText(resource.type),
		},
	};

	const config = powerStateConfig[stateKey] || powerStateConfig[PowerState.UNKNOWN];
	const IconComponent = config.icon;

	return (
		<ShineWrapper shouldShowAnimation={stateKey === PowerState.READY}>
			<BadgeWithTooltip
				icon={<IconComponent size={iconSize} color={palette.white} />}
				backgroundColor={config.backgroundColor}
				text={config.text}
				tooltipText={powerStateTimeStamp}
				onMouseEnter={handlePowerStatusAge}
			/>
		</ShineWrapper>
	);
};
